import { useCallback, useContext, useRef } from 'react';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  GROUP_FEATURE_DISCOVERED,
  GroupFeatureDiscovered,
  GroupFeaturesDiscoveredEventProps,
} from 'models/MixpanelEvents';

// Drives the discovery hints (pulsating dot + info card) on the DR members
// table: one on the member row and one on the group pill. Each "seen" flag is
// persisted per user, so the hints keep showing to new users but never
// re-appear once discovered. There is deliberately no release-date expiry.
export default function useGroupFeatureDiscovery() {
  const { currentUser, updatePreferences } = useContext(CurrentUserContext);

  const { seenGroupRowFeature, seenGroupNameFeature } = currentUser ?? {};

  const getMixpanelProps = useGetMixpanelProps();

  // Keep the latest "seen" flags in a ref so the discover callbacks stay
  // stable — they feed table cell renderers, and a changing identity would
  // remount the cells.
  const seenRef = useRef({ seenGroupRowFeature, seenGroupNameFeature });
  seenRef.current = { seenGroupRowFeature, seenGroupNameFeature };

  const discover = useCallback(
    (feature: GroupFeatureDiscovered) => {
      const isGroupRow = feature === GROUP_FEATURE_DISCOVERED.GROUP_ROW;

      const alreadySeen = isGroupRow
        ? seenRef.current.seenGroupRowFeature
        : seenRef.current.seenGroupNameFeature;

      if (alreadySeen) return;

      // Mark it locally straight away: a single click can reach two discovery
      // paths (dismissing the card, then the row / pill handler underneath),
      // and `seenRef` only catches up on the next render.
      seenRef.current = isGroupRow
        ? { ...seenRef.current, seenGroupRowFeature: true }
        : { ...seenRef.current, seenGroupNameFeature: true };

      updatePreferences(
        isGroupRow
          ? { seenGroupRowFeature: true }
          : { seenGroupNameFeature: true },
      );

      mixpanelService.groupFeaturesDiscovered(
        getMixpanelProps({
          featureDiscovered: feature,
        }) as GroupFeaturesDiscoveredEventProps,
      );
    },
    [getMixpanelProps, updatePreferences],
  );

  const discoverGroupRow = useCallback(
    () => discover(GROUP_FEATURE_DISCOVERED.GROUP_ROW),
    [discover],
  );

  const discoverGroupName = useCallback(
    () => discover(GROUP_FEATURE_DISCOVERED.GROUP_NAME),
    [discover],
  );

  return {
    shouldPulseGroupRow: !seenGroupRowFeature,
    shouldPulseGroupName: !seenGroupNameFeature,
    discoverGroupRow,
    discoverGroupName,
  };
}
